class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const term = this.getQuery();

    let searchTerms = localStorage.getItem("searchTerms");
    if (!searchTerms) {
      searchTerms = [];
    } else {
      searchTerms = JSON.parse(searchTerms);
    }

    const existingIndex = searchTerms.indexOf(term);
    if (existingIndex !== -1) {
      searchTerms.splice(existingIndex, 1);
    }

    if (searchTerms.length >= 5) {
      searchTerms.shift();
    }

    searchTerms.push(term);

    localStorage.setItem("searchTerms", JSON.stringify(searchTerms));

    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();

    // initialize recent searches
    let searchTerms = localStorage.getItem("searchTerms");
    if (searchTerms && searchTerms.length > 2) {
      searchTerms = JSON.parse(searchTerms);

      document.getElementById("recentSearchesWrapper").innerHTML = searchTerms
        .map((term) => {
          return `
          <div class="flex items-center justify-between w-full cursor-pointer recent-search-item px-2 py-1.5 rounded-[4px]">
            <div class="flex items-center justify-start gap-x-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4.25 8L2.75 9.5L1.25 8" stroke="#555E69" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M2.74914 8C2.74914 8.46267 2.80647 8.91067 2.90581 9.34333" stroke="#555E69" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M14.75 8C14.75 4.686 12.064 2 8.75 2C5.436 2 2.75 4.686 2.75 8" stroke="#555E69" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8.75 14C12.064 14 14.75 11.314 14.75 8" stroke="#555E69" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M3.83594 11.4365C4.9206 12.9852 6.71527 13.9999 8.74994 13.9999" stroke="#555E69" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10.9972 9.91391L8.5625 8.46524V5.32324" stroke="#555E69" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <li class="recentTerm">${term}</li>
            </div>
            <svg class="recentSearchRemove" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.375 2.625L2.625 9.375M9.375 9.375L2.625 2.625" stroke="#051122" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        `;
        })
        .join("");

      document
        .getElementById("recentSearchesWrapper")
        .parentElement.classList.remove("hidden");

      document.querySelectorAll(".recent-search-item").forEach((item) => {
        item.addEventListener("click", () => {
          const inputField = document.querySelector(
            ".search__input.field__input"
          );
          inputField.value = item
            .querySelector(".recentTerm")
            .textContent.trim();
          this.getSearchResults(
            item.querySelector(".recentTerm").textContent.trim()
          );
          window.location.href = `/search?q=${item
            .querySelector(".recentTerm")
            .textContent.trim()}`;
        });
      });

      document
        .querySelectorAll(".recentSearchRemove")
        .forEach((item, i, items) => {
          item.addEventListener("click", (e) => {
            e.stopPropagation();

            const searchItem = item.parentElement
              .querySelector(".recentTerm")
              .textContent.trim();
            searchTerms = searchTerms.filter((term) => term !== searchItem);
            localStorage.setItem("searchTerms", JSON.stringify(searchTerms));
            item.parentElement.remove();

            if (searchTerms.length === 0) {
              document
                .getElementById("recentSearchesWrapper")
                .parentElement.classList.add("hidden");
            }
          });
        });
    }

    // intialize trending searches
    const predictiveSearchHeader = document.querySelector(
      ".predictive-search.predictive-search--header.with-keywords"
    );
    let keyWords = predictiveSearchHeader.dataset.keywords;
    keyWords = keyWords.split(",");

    document.getElementById("keyWordsWrapper").innerHTML = keyWords
      .map((keyword) => {
        return `<li class="trending-search-item">${keyword}</li>`;
      })
      .join("");

    document.querySelectorAll(".trending-search-item").forEach((item) => {
      item.addEventListener("click", (e) => {
        const inputField = document.querySelector(
          ".search__input.field__input"
        );
        inputField.value = e.target.textContent;
        this.getSearchResults(e.target.textContent);
      });
    });

    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    document.querySelector(".searchFade").classList.add("md:block");
    if (window.innerWidth < 770) {
      document.body.style.overflow = "hidden";
    }
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    document.querySelector(".searchFade").classList.remove("md:block");
    if (window.innerWidth < 770) {
      document.body.style.overflow = "auto";
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);
